import { getAllProjects, getAllCategories, getAllFolders, getAllLists } from '@/db/projects';
import type { Space as Project, Folder, ListItem, Category } from '@/types';
import type { Task } from '@/db';
import {
  getAllCachedStructure,
  getStructureCacheMetadata,
  syncStructureFromSupabase,
  isIndexedDBAvailable,
} from './structureCache';
import { logger } from '@/utils/logger';
import { CACHE_MAX_AGE_MS } from '@/constants/timings';

/**
* Organize projects, folders, and lists from the database.
* This is the new way to organize data with Supabase - directly from the database
* instead of deriving from tasks.
* Optionally includes task counts if tasks are provided.
* Uses cached data first for instant loading, then syncs from Supabase in background.
*/
export async function organizeProjectsFromDatabase(tasks?: Array<Task & { id: string }>, options?: { forceRefresh?: boolean; orgId?: string }): Promise<Project[]> {
  const forceRefresh = options?.forceRefresh ?? false;
  const orgId = options?.orgId;
  let projects: Array<{ id: string; name: string; description?: string }>;
  let folders: Array<{ id: string; name: string; project_id: string }>;
  let lists: Array<{ id: string; name: string; project_id: string; folder_id: string | null }>;

  // When org filtering is active, always query fresh with org filter
  if (orgId) {
    projects = await getAllProjects(orgId);
    [folders, lists] = await Promise.all([
      getAllFolders(projects),
      getAllLists(projects),
    ]);
  } else {

  // Try to load from cache first for instant rehydration
  const loadStartTime = performance.now();
  if (isIndexedDBAvailable() && !forceRefresh) {
  try {
      const [cachedStructure, metadata] = await Promise.all([
    getAllCachedStructure(),
    getStructureCacheMetadata(),
      ]);
      const { spaces: cachedSpaces, folders: cachedFolders, lists: cachedLists } = cachedStructure;

      const cacheAge = metadata ? Date.now() - metadata.lastSyncTime : Infinity;

      // Use cache if it exists and is fresh
      if (cachedSpaces.length > 0 && cacheAge < CACHE_MAX_AGE_MS) {
    projects = cachedSpaces;
    folders = cachedFolders.map(f => ({ id: f.id, name: f.name, project_id: f.project_id }));
    lists = cachedLists.map(l => ({ id: l.id, name: l.name, project_id: l.project_id, folder_id: l.folder_id }));
    const loadTime = performance.now() - loadStartTime;
    logger.debug(`[Cache] Loaded structure from cache in ${loadTime.toFixed(2)}ms (${projects.length} projects, ${folders.length} folders, ${lists.length} lists)`);

    // If cached folders/lists are empty, fetch them synchronously
    if (cachedFolders.length === 0 && cachedLists.length === 0) {
          [folders, lists] = await Promise.all([
      getAllFolders(projects),
      getAllLists(projects),
          ]);
    }

    // Sync in background to keep cache fresh (also verifies deleted projects)
    if (cacheAge > CACHE_MAX_AGE_MS * 0.7) {
          syncStructureFromSupabase(getAllProjects, getAllFolders, getAllLists).catch(logger.error);
    }
      } else {
    // Cache is empty or expired, fetch from Supabase
    projects = await getAllProjects();
    [folders, lists] = await Promise.all([
          getAllFolders(projects),
          getAllLists(projects),
    ]);
    await syncStructureFromSupabase(getAllProjects, getAllFolders, getAllLists).catch(logger.error);
      }
  } catch (error) {
      logger.error('[Cache] Failed to load from cache, falling back to Supabase:', error);
      projects = await getAllProjects();
      [folders, lists] = await Promise.all([
    getAllFolders(projects),
    getAllLists(projects),
      ]);
      syncStructureFromSupabase(getAllProjects, getAllFolders, getAllLists).catch(logger.error);
  }
  } else {
  projects = await getAllProjects();
  [folders, lists] = await Promise.all([
      getAllFolders(projects),
      getAllLists(projects),
  ]);
  }

  } // end orgId else block

  // Count tasks per container (spaceId can be project, folder, or list)
  const taskCountsByProjectId = new Map<string, number>();
  if (tasks) {
  tasks.forEach(task => {
      // Count by spaceId directly (spaceId now refers to any container: project/folder/list)
      const spaceId = task.spaceId;
      if (spaceId) {
    // Convert to string if needed for comparison
    const projectIdStr = String(spaceId);
    taskCountsByProjectId.set(projectIdStr, (taskCountsByProjectId.get(projectIdStr) || 0) + 1);
      }
  });
  }

  // Create maps for quick lookup
  const foldersByProjectId = new Map<string, Array<{ id: string; name: string; orderIndex?: number }>>();
  const listsByFolderId = new Map<string, Array<{ id: string; name: string; orderIndex?: number }>>();
  const listsByProjectId = new Map<string, Array<{ id: string; name: string; folder_id: string | null; orderIndex?: number }>>();

  // Organize folders by project, preserving orderIndex
  folders.forEach(folder => {
  if (!foldersByProjectId.has(folder.project_id)) {
      foldersByProjectId.set(folder.project_id, []);
  }
  const orderIndex = 'orderIndex' in folder && typeof folder.orderIndex === 'number' ? folder.orderIndex : undefined;
  foldersByProjectId.get(folder.project_id)!.push({ id: folder.id, name: folder.name, orderIndex });
  });
  
  // Sort folders by orderIndex within each project
  foldersByProjectId.forEach((folderList) => {
  folderList.sort((a, b) => {
      const orderA = a.orderIndex ?? Infinity;
      const orderB = b.orderIndex ?? Infinity;
      if (orderA !== orderB) return orderA - orderB;
      return a.name.localeCompare(b.name);
  });
  });

  // Organize lists by folder and project, preserving orderIndex
  lists.forEach(list => {
  const orderIndex = 'orderIndex' in list && typeof list.orderIndex === 'number' ? list.orderIndex : undefined;
  if (list.folder_id) {
      if (!listsByFolderId.has(list.folder_id)) {
    listsByFolderId.set(list.folder_id, []);
      }
      listsByFolderId.get(list.folder_id)!.push({ id: list.id, name: list.name, orderIndex });
  } else {
      if (!listsByProjectId.has(list.project_id)) {
    listsByProjectId.set(list.project_id, []);
      }
      listsByProjectId.get(list.project_id)!.push({ id: list.id, name: list.name, folder_id: null, orderIndex });
  }
  });
  
  // Sort lists by orderIndex within each folder/project
  listsByFolderId.forEach((listList) => {
  listList.sort((a, b) => {
      const orderA = a.orderIndex ?? Infinity;
      const orderB = b.orderIndex ?? Infinity;
      if (orderA !== orderB) return orderA - orderB;
      return a.name.localeCompare(b.name);
  });
  });
  
  listsByProjectId.forEach((listList) => {
  listList.sort((a, b) => {
      const orderA = a.orderIndex ?? Infinity;
      const orderB = b.orderIndex ?? Infinity;
      if (orderA !== orderB) return orderA - orderB;
      return a.name.localeCompare(b.name);
  });
  });

  // Build the Project structure
  const result: Project[] = projects.map(project => {
  const projectFolders: Folder[] = [];
  const rootLists: ListItem[] = [];
  let projectTaskCount = 0;

  // Get folders for this project
  const projectFolderList = foldersByProjectId.get(project.id) || [];

  // Build folders with their lists
  projectFolderList.forEach(folder => {
      const folderLists = listsByFolderId.get(folder.id) || [];
      let folderTaskCount = 0;

      const listItems: ListItem[] = folderLists.map(list => {
    const count = taskCountsByProjectId.get(list.id) || 0;
    folderTaskCount += count;
    return {
          id: list.id,
          name: list.name,
          taskCount: count,
    };
      });

      // Add direct tasks in the folder itself
      folderTaskCount += taskCountsByProjectId.get(folder.id) || 0;
      projectTaskCount += folderTaskCount;

      projectFolders.push({
    id: folder.id,
    name: folder.name,
    lists: listItems,
    taskCount: folderTaskCount,
      });
  });

  // Get root lists (lists without folders) for this project
  const rootListsData = listsByProjectId.get(project.id) || [];
  let rootTaskCount = 0;
  rootListsData.forEach(list => {
      const count = taskCountsByProjectId.get(list.id) || 0;
      rootTaskCount += count;
      rootLists.push({
    id: list.id,
    name: list.name,
    taskCount: count,
      });
  });

  projectTaskCount += rootTaskCount;

  // Add tasks that live directly in the project (no list/folder)
  const projectIdStr = String(project.id);
  const directInProject = taskCountsByProjectId.get(projectIdStr) || 0;
  projectTaskCount += directInProject;

  return {
      id: project.id,
      name: project.name,
      description: project.description,
      parent_id: (project as { parent_id?: string | null }).parent_id ?? undefined,
      folders: projectFolders,
      rootLists,
      taskCount: projectTaskCount,
  } as Project;
  });

  // Remove duplicates by name (keep the first one encountered)
  const uniqueProjects = new Map<string, Project>();
  result.forEach(project => {
  if (!uniqueProjects.has(project.name)) {
      uniqueProjects.set(project.name, project);
  } else {
      // If duplicate found, keep the one with higher taskCount
      const existing = uniqueProjects.get(project.name)!;
      if (project.taskCount > existing.taskCount) {
    uniqueProjects.set(project.name, project);
      }
  }
  });

  // Preserve order from database (already sorted by orderIndex)
  // Only remove duplicates, don't re-sort
  const finalResult = Array.from(uniqueProjects.values());
  return finalResult;
}

/**
 * Organize categories by grouping projects into their parent categories.
 * Projects with parent_id matching a category ID are grouped into that category.
 * Projects without a matching category are collected into an "Altro" fallback.
 */
export function organizeCategories(
  categories: Array<{ id: string; name: string }>,
  projects: Project[],
): Category[] {
  const categoryMap = new Map<string, Project[]>();
  categories.forEach(c => categoryMap.set(c.id, []));

  const uncategorized: Project[] = [];

  for (const project of projects) {
    const catId = project.parent_id;
    if (catId && categoryMap.has(catId)) {
      categoryMap.get(catId)!.push(project);
    } else {
      uncategorized.push(project);
    }
  }

  const result: Category[] = categories.map(c => {
    const catProjects = categoryMap.get(c.id) || [];
    return {
      id: c.id,
      name: c.name,
      projects: catProjects,
      taskCount: catProjects.reduce((sum, p) => sum + (p.taskCount || 0), 0),
    };
  });

  // If there are uncategorized projects, add them as a fallback category
  if (uncategorized.length > 0) {
    result.push({
      id: 'uncategorized',
      name: 'Altro',
      projects: uncategorized,
      taskCount: uncategorized.reduce((sum, p) => sum + (p.taskCount || 0), 0),
    });
  }

  return result;
}

/**
 * Recompute category task counts from their contained projects.
 * Preserves object identity when counts haven't changed.
 */
export function recomputeCategoryTaskCounts(categories: Category[]): Category[] {
  return categories.map(cat => {
    const newCount = cat.projects.reduce((sum, p) => sum + (p.taskCount || 0), 0);
    if (newCount === cat.taskCount) return cat;
    return { ...cat, taskCount: newCount };
  });
}

/**
 * Fetch categories from database alongside projects.
 */
export async function fetchCategoriesFromDatabase(orgId?: string): Promise<Array<{ id: string; name: string }>> {
  return getAllCategories(orgId);
}

/**
 * Lightweight recomputation of task counts from in-memory tasks.
 * Preserves object identity when counts haven't changed to minimize React re-renders.
 */
export function recomputeTaskCounts(
  spaces: Project[],
  tasks: Array<{ spaceId?: string | null }>,
): Project[] {
  // 1. Count tasks per container ID
  const counts = new Map<string, number>();
  for (const task of tasks) {
    if (task.spaceId) {
      const id = String(task.spaceId);
      counts.set(id, (counts.get(id) || 0) + 1);
    }
  }

  // 2. Patch counts preserving object identity
  return spaces.map(space => {
    let total = counts.get(space.id) || 0; // tasks directly in the space
    let foldersChanged = false;

    const newFolders = space.folders.map(folder => {
      let folderTotal = folder.id ? (counts.get(folder.id) || 0) : 0;
      let listsChanged = false;

      const newLists = folder.lists.map(list => {
        const c = counts.get(list.id) || 0;
        folderTotal += c;
        if (c === list.taskCount) return list; // same ref
        listsChanged = true;
        return { ...list, taskCount: c };
      });

      total += folderTotal;
      if (!listsChanged && folderTotal === folder.taskCount) return folder;
      foldersChanged = true;
      return { ...folder, lists: listsChanged ? newLists : folder.lists, taskCount: folderTotal };
    });

    // Root lists (from space.rootLists)
    let rootListsChanged = false;
    const newRootLists = (space.rootLists || []).map(list => {
      const c = counts.get(list.id) || 0;
      total += c;
      if (c === list.taskCount) return list;
      rootListsChanged = true;
      return { ...list, taskCount: c };
    });

    if (!foldersChanged && !rootListsChanged && total === space.taskCount) return space; // same ref
    return {
      ...space,
      folders: foldersChanged ? newFolders : space.folders,
      rootLists: rootListsChanged ? newRootLists : space.rootLists,
      taskCount: total,
    };
  });
}

